// Feature: docs/features/nma-qa-digest-chat.md
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { InfoIcon } from '@chakra-ui/icons';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Flex,
  Input,
  Button,
  HStack,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  UnorderedList,
  ListItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  VStack
} from '@chakra-ui/react';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import ModernChat from './ModernChat';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const NMACodingQualityDashboard = () => {
  const { accessToken } = useContext(AppContext);
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  
  // Default to last 7 days
  // Default to last 7 days, excluding today to ensure clean caching
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const defaultEndDate = yesterday.toISOString().split('T')[0];
  const defaultStartDate = new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  const [filters, setFilters] = useState({
    start_date: defaultStartDate,
    end_date: defaultEndDate,
    technologist: '',
    surgeon: ''
  });

  const fetchData = async (overrides = {}) => {
    if (!accessToken) return;
    
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      const p_start = overrides.start_date !== undefined ? overrides.start_date : filters.start_date;
      const p_end = overrides.end_date !== undefined ? overrides.end_date : filters.end_date;
      const p_tech = overrides.technologist !== undefined ? overrides.technologist : filters.technologist;
      const p_surg = overrides.surgeon !== undefined ? overrides.surgeon : filters.surgeon;

      if (p_start) queryParams.append('start_date', p_start);
      if (p_end) queryParams.append('end_date', p_end);
      if (p_tech) queryParams.append('technologist', p_tech);
      if (p_surg) queryParams.append('surgeon', p_surg);

      const queryString = queryParams.toString();
      const endpoint = queryString ? `/api/nma/coding-quality?${queryString}` : '/api/nma/coding-quality';

      const response = await callApi(endpoint, accessToken, { method: 'GET' });
      
      if (response.ok) {
        const result = await response.json();
        if (result.status === 'success') {
          setData(result.data);
        } else {
          setError(result.message || 'Failed to fetch data');
        }
      } else {
        setError(`API Error: ${response.status}`);
      }
    } catch (err) {
      console.error("Error fetching coding quality data:", err);
      setError('An error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, filters.start_date, filters.end_date]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleClearFilters = () => {
    setFilters({
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      technologist: '',
      surgeon: ''
    });
    fetchData({
      start_date: defaultStartDate,
      end_date: defaultEndDate,
      technologist: '',
      surgeon: ''
    });
  };

  const handleTechnologistClick = (techName) => {
    const newTech = techName === 'Unknown' ? '' : techName;
    setFilters(prev => ({ ...prev, technologist: newTech }));
    setTabIndex(0); // Switch back to Overview tab
    fetchData({ technologist: newTech });
  };

  // --- Data Processing for Charts and KPIs ---
  const processedData = useMemo(() => {
    const totalCases = data.length;
    let needsReviewCount = 0;
    let preliminaryCount = 0;
    
    const dailyMap = {};
    const issuesMap = {};
    const techSummaryMap = {};
    const admissionStatusMap = {};
    const admissionConfidenceMap = {};
    let cadwellDataCount = 0;

    data.forEach(item => {
      if (item.has_cadwell_data) cadwellDataCount++;
      if (item.needs_review) needsReviewCount++;
      if (item.has_preliminary) preliminaryCount++;

      // Admission Status Aggregation
      // Use recommended_pos_code if available, otherwise fallback to status string
      let status = item.recommended_pos_code && item.recommended_pos_code !== 'Missing' 
        ? `POS ${item.recommended_pos_code}` 
        : (item.admission_status || 'Missing');
        
      admissionStatusMap[status] = (admissionStatusMap[status] || 0) + 1;

      const confidence = item.admission_confidence || 'Missing';
      admissionConfidenceMap[confidence] = (admissionConfidenceMap[confidence] || 0) + 1;

      // Daily Trend (using date_of_surgery, fallback to created_at)
      let dateStr = 'Unknown';
      if (item.date_of_surgery) {
        // Convert MM/DD/YYYY to YYYY-MM-DD for sorting
        const parts = item.date_of_surgery.split('/');
        if (parts.length === 3) {
          dateStr = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        } else {
          dateStr = item.date_of_surgery;
        }
      } else if (item.created_at) {
        dateStr = item.created_at.split('T')[0];
      }

      if (!dailyMap[dateStr]) {
        dailyMap[dateStr] = { 
          date: dateStr, 
          Clean: 0, 
          'Needs Review': 0, 
          'Preliminary': 0, 
          total: 0, 
          withCadwell: 0,
          missingPayerCount: 0,
          posIssueCount: 0
        };
      }
      
      dailyMap[dateStr].total++;
      if (item.has_cadwell_data) dailyMap[dateStr].withCadwell++;

      if (item.needs_review) {
        dailyMap[dateStr]['Needs Review']++;
      } else if (item.has_preliminary) {
        dailyMap[dateStr]['Preliminary']++;
      } else {
        dailyMap[dateStr]['Clean']++;
      }

      // Check for POS Issue
      const conf = parseFloat(item.admission_confidence);
      if (!isNaN(conf) && conf < 8) {
        dailyMap[dateStr].posIssueCount++;
      }

      // Check for Missing Payer (using source of truth flag)
      if (item.is_missing_payer) {
        dailyMap[dateStr].missingPayerCount++;
      }

      // Top Issues
      if (item.cpt_issues && item.cpt_issues.length > 0) {
        item.cpt_issues.forEach(issue => {
          const key = `${issue.cpt_code} - ${issue.issue_category}`;
          issuesMap[key] = (issuesMap[key] || 0) + 1;
        });
      }

      // Technologist Summary
      const tech = item.technologist_name || 'Unknown';
      if (!techSummaryMap[tech]) {
        techSummaryMap[tech] = { name: tech, total: 0, needsReview: 0, preliminary: 0, withCadwell: 0 };
      }
      techSummaryMap[tech].total++;
      if (item.needs_review) techSummaryMap[tech].needsReview++;
      if (item.has_preliminary) techSummaryMap[tech].preliminary++;
      if (item.has_cadwell_data) techSummaryMap[tech].withCadwell++;
    });

    const parseDate = (dateStr) => {
      const [y, m, d] = dateStr.split('-');
      return new Date(y, m - 1, d);
    };

    const sortedDailyTrend = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));

    const dailyTrend = sortedDailyTrend.map((d, index, arr) => {
      const currentDate = parseDate(d.date);
      let rollingTotal = 0;
      let rollingNeedsReview = 0;
      let rollingMissingPayer = 0;
      let rollingPosIssue = 0;

      for (let i = index; i >= 0; i--) {
        const pastDay = arr[i];
        const pastDate = parseDate(pastDay.date);
        const diffTime = currentDate.getTime() - pastDate.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        
        if (diffDays < 7) {
          rollingTotal += pastDay.total;
          rollingNeedsReview += (pastDay['Needs Review'] + pastDay['Preliminary']);
          rollingMissingPayer += pastDay.missingPayerCount;
          rollingPosIssue += pastDay.posIssueCount;
        } else {
          break;
        }
      }

      return {
        ...d,
        withoutCadwell: d.total - d.withCadwell,
        cadwellPct: d.total > 0 ? Number(((d.withCadwell / d.total) * 100).toFixed(1)) : 0,
        missingPayerPct: rollingTotal > 0 ? Number(((rollingMissingPayer / rollingTotal) * 100).toFixed(1)) : 0,
        posIssuePct: rollingTotal > 0 ? Number(((rollingPosIssue / rollingTotal) * 100).toFixed(1)) : 0,
        needsReviewPct: rollingTotal > 0 ? Number(((rollingNeedsReview / rollingTotal) * 100).toFixed(1)) : 0
      };
    });
    
    const topIssues = Object.entries(issuesMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Top 10

    const flaggedCases = data.filter(d => d.needs_review || d.has_preliminary);

    const techSummary = Object.values(techSummaryMap).map(tech => {
      const passedCases = tech.total - tech.needsReview;
      const passedCodes = tech.total - tech.preliminary;
      return {
        ...tech,
        passedCases,
        passedCodes,
        passedCasePct: tech.total > 0 ? ((passedCases / tech.total) * 100).toFixed(1) : 0,
        passedCodePct: tech.total > 0 ? ((passedCodes / tech.total) * 100).toFixed(1) : 0,
        cadwellPct: tech.total > 0 ? ((tech.withCadwell / tech.total) * 100).toFixed(1) : 0
      };
    }).sort((a, b) => b.total - a.total); // Sort by volume descending

    const passedCasesTotal = totalCases - needsReviewCount;
    const passedCodesTotal = totalCases - preliminaryCount;

    // Compile notes for the QA Digest Chat
    const needsReviewCases = flaggedCases.filter(c => c.needs_review);
    const qaDigestNotes = needsReviewCases.map(c => {
      return `Case ID: ${c.tech_report_case_id || c.external_case_id}\nDate: ${c.date_of_surgery || c.created_at}\nNotes:\n${c.claim_notes || 'No notes provided.'}`;
    }).join('\n\n---\n\n');

    // Format Admission Status Data for Recharts
    const admissionStatusChartData = Object.keys(admissionStatusMap).map(key => ({
      name: key,
      count: admissionStatusMap[key]
    })).sort((a, b) => b.count - a.count);

    // Format Admission Confidence Data for Recharts (sort numerically, put 'Missing' at end)
    const admissionConfidenceChartData = Object.keys(admissionConfidenceMap).map(key => ({
      name: key,
      count: admissionConfidenceMap[key]
    })).sort((a, b) => {
      if (a.name === 'Missing') return 1;
      if (b.name === 'Missing') return -1;
      return parseInt(a.name) - parseInt(b.name);
    });

    return {
      kpis: {
        total: totalCases,
        passedCasePct: totalCases ? ((passedCasesTotal / totalCases) * 100).toFixed(1) : 0,
        passedCodePct: totalCases ? ((passedCodesTotal / totalCases) * 100).toFixed(1) : 0,
        cadwellPct: totalCases ? ((cadwellDataCount / totalCases) * 100).toFixed(1) : 0
      },
      dailyTrend,
      topIssues,
      flaggedCases,
      techSummary,
      needsReviewCases,
      qaDigestNotes,
      admissionStatusChartData,
      admissionConfidenceChartData
    };
  }, [data]);

  const exportToCSV = () => {
    if (!processedData.flaggedCases || processedData.flaggedCases.length === 0) return;

    const headers = [
      'Date',
      'Case ID',
      'Case Link',
      'Cadwell Data',
      'Technologist',
      'Surgeon',
      'Flag Type',
      'Details'
    ];

    const escapeCSV = (str) => {
      if (str === null || str === undefined) return '""';
      const stringified = String(str);
      if (stringified.includes(',') || stringified.includes('"') || stringified.includes('\n')) {
        return `"${stringified.replace(/"/g, '""')}"`;
      }
      return `"${stringified}"`;
    };

    const csvRows = processedData.flaggedCases.map(item => {
      const date = item.date_of_surgery || (item.created_at ? item.created_at.split('T')[0] : 'N/A');
      const caseId = item.tech_report_case_id || item.external_case_id;
      const caseLink = `https://app.revedy.io/ionmform?caseid=${item.external_case_id}`;
      const tech = item.technologist_name || 'N/A';
      const surg = item.referring_provider || 'N/A';
      
      const flagType = [];
      if (item.needs_review) flagType.push('Claim Review');
      if (item.has_preliminary) flagType.push('Row Preliminary');

      let details = '';
      if (item.needs_review && item.claim_notes) {
        details += `Claim Note: ${item.claim_notes} | `;
      }
      if (item.has_preliminary && item.cpt_issues && item.cpt_issues.length > 0) {
        details += `CPT Issues: ${item.cpt_issues.map(i => `${i.cpt_code} - ${i.issue_category}`).join(', ')}`;
      }

      return [
        escapeCSV(date),
        escapeCSV(caseId),
        escapeCSV(caseLink),
        escapeCSV(item.has_cadwell_data ? 'Yes' : 'No'),
        escapeCSV(tech),
        escapeCSV(surg),
        escapeCSV(flagType.join(' & ')),
        escapeCSV(details)
      ].join(',');
    });

    const csvContent = [headers.join(','), ...csvRows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `NMA_Coding_Quality_Export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1400px" margin="0 auto">
        <Flex justify="space-between" align="center" mb={6}>
          <Heading size="lg" color="gray.800">NMA Coding Quality Dashboard</Heading>
          <Button
            as={RouterLink}
            to="/nma-guidelines"
            colorScheme="blue"
            variant="outline"
            size="sm"
            leftIcon={<InfoIcon />}
          >
            View Coding Guidelines
          </Button>
        </Flex>

        {/* Filters */}
        <Box bg="white" p={4} borderRadius="md" shadow="sm" mb={6} borderWidth="1px">
          <form onSubmit={handleFilterSubmit}>
            <Flex gap={4} flexWrap="wrap" alignItems="flex-end">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Start Date</Text>
                <Input 
                  type="date" 
                  name="start_date" 
                  value={filters.start_date} 
                  onChange={handleFilterChange}
                  size="sm" 
                />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>End Date</Text>
                <Input 
                  type="date" 
                  name="end_date" 
                  value={filters.end_date} 
                  onChange={handleFilterChange}
                  size="sm" 
                />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Technologist</Text>
                <Input 
                  type="text" 
                  name="technologist" 
                  placeholder="Filter by tech..."
                  value={filters.technologist} 
                  onChange={handleFilterChange}
                  size="sm" 
                />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={1}>Surgeon</Text>
                <Input 
                  type="text" 
                  name="surgeon" 
                  placeholder="Filter by surgeon..."
                  value={filters.surgeon} 
                  onChange={handleFilterChange}
                  size="sm" 
                />
              </Box>
              <HStack>
                <Button type="submit" colorScheme="blue" size="sm">Apply Filters</Button>
                <Button onClick={handleClearFilters} variant="outline" size="sm">Clear</Button>
                <Button 
                  onClick={exportToCSV} 
                  colorScheme="green" 
                  size="sm" 
                  isDisabled={processedData.flaggedCases.length === 0}
                >
                  Export CSV
                </Button>
              </HStack>
            </Flex>
          </form>
        </Box>

        {error && (
          <Box bg="red.100" color="red.700" p={3} borderRadius="md" mb={4}>
            {error}
          </Box>
        )}

        {loading ? (
          <Flex justify="center" p={10}>
            <Spinner size="xl" />
          </Flex>
        ) : (
          <>
            {/* KPIs (Always visible) */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                <Stat>
                  <StatLabel color="gray.500">Total Cases Coded</StatLabel>
                  <StatNumber fontSize="2xl">{processedData.kpis.total}</StatNumber>
                </Stat>
              </Box>
              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                <Stat>
                  <StatLabel color="gray.500">% Cases Passed Automated Review</StatLabel>
                  <StatNumber fontSize="2xl" color={processedData.kpis.passedCasePct < 100 ? "red.500" : "green.500"}>
                    {processedData.kpis.passedCasePct}%
                  </StatNumber>
                </Stat>
              </Box>
              <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                <Stat>
                  <StatLabel color="gray.500">% Codes Passed Automated Review</StatLabel>
                  <StatNumber fontSize="2xl" color={processedData.kpis.passedCodePct < 100 ? "yellow.500" : "green.500"}>
                    {processedData.kpis.passedCodePct}%
                  </StatNumber>
                </Stat>
              </Box>
            </SimpleGrid>

            <Tabs variant="enclosed" colorScheme="blue" index={tabIndex} onChange={(index) => setTabIndex(index)}>
              <TabList>
                <Tab>Overview</Tab>
                <Tab>Technologist Summary</Tab>
                <Tab>Cadwell Data</Tab>
                <Tab>Needs Review Digest</Tab>
                <Tab>POS</Tab>
              </TabList>

              <TabPanels>
                {/* Tab 1: Overview */}
                <TabPanel px={0} py={4}>
                  {/* Charts */}
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                      <Heading size="sm" mb={4} color="gray.600">Daily Trend</Heading>
                      <Box height="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={processedData.dailyTrend} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{fontSize: 12}} />
                            <YAxis tick={{fontSize: 12}} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="Clean" stackId="a" fill="#48BB78" />
                            <Bar dataKey="Preliminary" stackId="a" fill="#ECC94B" />
                            <Bar dataKey="Needs Review" stackId="a" fill="#F56565" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>

                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                      <Heading size="sm" mb={4} color="gray.600">Top CPT Issues (Row-Level)</Heading>
                      <Box height="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={processedData.topIssues} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                            <XAxis type="number" />
                            <YAxis dataKey="name" type="category" tick={{fontSize: 11}} width={90} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#4299E1" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </SimpleGrid>

                  <Box mb={6}>
                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
                      <Heading size="sm" mb={4} color="gray.600">7-Day Rolling Average: Total Needs Review (%)</Heading>
                      <Box height="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={processedData.dailyTrend} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{fontSize: 12}} />
                            <YAxis tick={{fontSize: 12}} tickFormatter={(val) => `${val}%`} />
                            <Tooltip formatter={(value) => `${value}%`} />
                            <Legend />
                            <Line type="linear" dataKey="needsReviewPct" name="Total Needs Review" stroke="#805AD5" strokeWidth={2} strokeDasharray="5 5" dot={{ stroke: '#805AD5', strokeWidth: 2, r: 4, fill: '#805AD5', shape: 'square' }} activeDot={{ r: 6, shape: 'square' }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>

                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
                      <Heading size="sm" mb={4} color="gray.600">7-Day Rolling Average: Missing Payer (%)</Heading>
                      <Box height="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={processedData.dailyTrend} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{fontSize: 12}} />
                            <YAxis tick={{fontSize: 12}} tickFormatter={(val) => `${val}%`} />
                            <Tooltip formatter={(value) => `${value}%`} />
                            <Legend />
                            <Line type="linear" dataKey="missingPayerPct" name="Missing Payer" stroke="#E53E3E" strokeWidth={2} dot={{ stroke: '#E53E3E', strokeWidth: 2, r: 4, fill: 'white' }} activeDot={{ r: 6 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>

                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                      <Heading size="sm" mb={4} color="gray.600">7-Day Rolling Average: POS Confidence &lt; 8 (%)</Heading>
                      <Box height="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={processedData.dailyTrend} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{fontSize: 12}} />
                            <YAxis tick={{fontSize: 12}} tickFormatter={(val) => `${val}%`} />
                            <Tooltip formatter={(value) => `${value}%`} />
                            <Legend />
                            <Line type="linear" dataKey="posIssuePct" name="POS Confidence < 8" stroke="#3182CE" strokeWidth={2} dot={{ stroke: '#3182CE', strokeWidth: 2, r: 4, fill: '#3182CE', shape: 'triangle' }} activeDot={{ r: 6, shape: 'triangle' }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </Box>

                  {/* Flagged Cases Table */}
                  <Box bg="white" borderRadius="md" shadow="sm" borderWidth="1px" overflow="hidden">
                    <Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.200">
                      <Heading size="sm" color="gray.800">Flagged Cases ({processedData.flaggedCases.length})</Heading>
                    </Box>
                    <Box overflowX="auto">
                      <Table variant="simple" size="sm">
                        <Thead bg="gray.50">
                          <Tr>
                            <Th>Date</Th>
                            <Th>Case ID</Th>
                            <Th>Cadwell Data</Th>
                            <Th>Provider Info</Th>
                            <Th>Flag Type</Th>
                            <Th>Details</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {processedData.flaggedCases.length === 0 ? (
                            <Tr>
                              <Td colSpan={6} textAlign="center" py={8} color="gray.500">
                                No flagged cases found for the selected filters.
                              </Td>
                            </Tr>
                          ) : (
                            processedData.flaggedCases.map((item, idx) => (
                              <Tr key={idx} _hover={{ bg: "gray.50" }}>
                                <Td whiteSpace="nowrap" color="gray.600">
                                  {item.date_of_surgery || (item.created_at ? item.created_at.split('T')[0] : 'N/A')}
                                </Td>
                                <Td whiteSpace="nowrap">
                                  <Text 
                                    as="a" 
                                    href={`/ionmform?caseid=${item.external_case_id}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    color="blue.500"
                                    fontWeight="medium"
                                    _hover={{ textDecoration: 'underline' }}
                                  >
                                    {item.tech_report_case_id || item.external_case_id.substring(0,8)+'...'}
                                  </Text>
                                </Td>
                                <Td>
                                  {item.has_cadwell_data ? (
                                    <Badge colorScheme="green">Yes</Badge>
                                  ) : (
                                    <Badge colorScheme="red">No</Badge>
                                  )}
                                </Td>
                                <Td color="gray.600">
                                  <Box><Text as="span" fontWeight="medium">Tech:</Text> {item.technologist_name || 'N/A'}</Box>
                                  <Box><Text as="span" fontWeight="medium">Surg:</Text> {item.referring_provider || 'N/A'}</Box>
                                </Td>
                                <Td whiteSpace="nowrap">
                                  <Flex direction="column" gap={1} align="flex-start">
                                    {item.needs_review && (
                                      <Badge colorScheme="red">Claim Review</Badge>
                                    )}
                                    {item.has_preliminary && (
                                      <Badge colorScheme="yellow">Row Preliminary</Badge>
                                    )}
                                  </Flex>
                                </Td>
                                <Td maxW="400px" whiteSpace="normal" color="gray.600">
                                  {item.needs_review && item.claim_notes && (
                                    <Box mb={2}>
                                      <Text as="strong" color="red.600" fontSize="xs">Claim Note:</Text>
                                      <Text fontSize="xs" noOfLines={3} title={item.claim_notes}>{item.claim_notes}</Text>
                                    </Box>
                                  )}
                                  {item.has_preliminary && item.cpt_issues && item.cpt_issues.length > 0 && (
                                    <Box>
                                      <Text as="strong" color="yellow.600" fontSize="xs">CPT Issues:</Text>
                                      <UnorderedList pl={4} fontSize="xs" m={0}>
                                        {item.cpt_issues.map((issue, i) => (
                                          <ListItem key={i}>{issue.cpt_code} - {issue.issue_category}</ListItem>
                                        ))}
                                      </UnorderedList>
                                    </Box>
                                  )}
                                </Td>
                              </Tr>
                            ))
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                </TabPanel>

                {/* Tab 2: Technologist Summary */}
                <TabPanel px={0} py={4}>
                  <Box overflowX="auto" bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Technologist</Th>
                          <Th isNumeric>Total Cases</Th>
                          <Th isNumeric>Cases Passed</Th>
                          <Th isNumeric>% Cases Passed</Th>
                          <Th isNumeric>Codes Passed</Th>
                          <Th isNumeric>% Codes Passed</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {processedData.techSummary.length === 0 ? (
                          <Tr>
                            <Td colSpan={6} textAlign="center" py={4}>No data available.</Td>
                          </Tr>
                        ) : (
                          processedData.techSummary.map((tech, index) => (
                            <Tr key={index} _hover={{ bg: "gray.50" }}>
                              <Td fontWeight="medium">
                                <Text 
                                  as="button" 
                                  color="blue.500" 
                                  _hover={{ textDecoration: 'underline' }} 
                                  onClick={() => handleTechnologistClick(tech.name)}
                                  textAlign="left"
                                >
                                  {tech.name}
                                </Text>
                              </Td>
                              <Td isNumeric>{tech.total}</Td>
                              <Td isNumeric>{tech.passedCases}</Td>
                              <Td>
                                <Flex align="center" justify="flex-end" gap={3}>
                                  <Text color={tech.passedCasePct < 100 ? "red.600" : "green.600"} fontWeight={tech.passedCasePct < 100 ? "bold" : "normal"} minW="45px" textAlign="right">
                                    {tech.passedCasePct}%
                                  </Text>
                                  <Progress 
                                    value={Number(tech.passedCasePct)} 
                                    colorScheme={tech.passedCasePct < 100 ? "red" : "green"} 
                                    size="sm" 
                                    width="100px" 
                                    borderRadius="md" 
                                    bg="gray.100"
                                  />
                                </Flex>
                              </Td>
                              <Td isNumeric>{tech.passedCodes}</Td>
                              <Td>
                                <Flex align="center" justify="flex-end" gap={3}>
                                  <Text color={tech.passedCodePct < 100 ? "yellow.600" : "green.600"} fontWeight={tech.passedCodePct < 100 ? "bold" : "normal"} minW="45px" textAlign="right">
                                    {tech.passedCodePct}%
                                  </Text>
                                  <Progress 
                                    value={Number(tech.passedCodePct)} 
                                    colorScheme={tech.passedCodePct < 100 ? "yellow" : "green"} 
                                    size="sm" 
                                    width="100px" 
                                    borderRadius="md" 
                                    bg="gray.100"
                                  />
                                </Flex>
                              </Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                </TabPanel>

                {/* Tab 3: Cadwell Data */}
                <TabPanel px={0} py={4}>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
                    <Box p={4} bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                      <Stat>
                        <StatLabel color="gray.500">Overall % with Cadwell Data</StatLabel>
                        <StatNumber fontSize="2xl" color={processedData.kpis.cadwellPct < 80 ? "red.500" : "green.500"}>
                          {processedData.kpis.cadwellPct}%
                        </StatNumber>
                      </Stat>
                    </Box>
                  </SimpleGrid>

                  <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" mb={6}>
                    <Heading size="sm" mb={4} color="gray.600">Daily Trend: Cadwell Data Volume</Heading>
                    <Box height="300px">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={processedData.dailyTrend} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="date" tick={{fontSize: 12}} />
                          <YAxis tick={{fontSize: 12}} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="withCadwell" name="With Cadwell" stackId="a" fill="#48BB78" />
                          <Bar dataKey="withoutCadwell" name="Without Cadwell" stackId="a" fill="#F56565" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>

                  <Box overflowX="auto" bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Technologist</Th>
                          <Th isNumeric>Total Cases</Th>
                          <Th isNumeric>With Cadwell Data</Th>
                          <Th isNumeric>% With Cadwell Data</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {processedData.techSummary.length === 0 ? (
                          <Tr>
                            <Td colSpan={4} textAlign="center" py={4}>No data available.</Td>
                          </Tr>
                        ) : (
                          processedData.techSummary.map((tech, index) => (
                            <Tr key={index} _hover={{ bg: "gray.50" }}>
                              <Td fontWeight="medium">
                                <Text 
                                  as="button" 
                                  color="blue.500" 
                                  _hover={{ textDecoration: 'underline' }} 
                                  onClick={() => handleTechnologistClick(tech.name)}
                                  textAlign="left"
                                >
                                  {tech.name}
                                </Text>
                              </Td>
                              <Td isNumeric>{tech.total}</Td>
                              <Td isNumeric>{tech.withCadwell}</Td>
                              <Td>
                                <Flex align="center" justify="flex-end" gap={3}>
                                  <Text color={tech.cadwellPct < 80 ? "red.600" : "green.600"} fontWeight={tech.cadwellPct < 80 ? "bold" : "normal"} minW="45px" textAlign="right">
                                    {tech.cadwellPct}%
                                  </Text>
                                  <Progress 
                                    value={Number(tech.cadwellPct)} 
                                    colorScheme={tech.cadwellPct < 80 ? "red" : "green"} 
                                    size="sm" 
                                    width="100px" 
                                    borderRadius="md" 
                                    bg="gray.100"
                                  />
                                </Flex>
                              </Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                </TabPanel>

                {/* Tab 4: Needs Review Digest */}
                <TabPanel px={0} py={4}>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                    {/* Left Column: AI Summary Chat */}
                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px" height="600px" display="flex" flexDirection="column">
                      <Heading size="sm" mb={4} color="gray.600">AI QA Summary</Heading>
                      <Text fontSize="sm" color="gray.500" mb={4}>
                        Ask the AI to summarize the recurring issues in the flagged cases below. Try: "Please summarize the recurring issues in these QA notes."
                      </Text>
                      <Box flex="1" overflow="hidden">
                        {processedData.qaDigestNotes ? (
                          <ModernChat 
                            caseId="dashboard-summary"
                            contextType="nma_qa_summary"
                            additionalContext={processedData.qaDigestNotes}
                            height="100%"
                          />
                        ) : (
                          <Flex justify="center" align="center" height="100%">
                            <Text color="gray.500">No QA notes available to summarize.</Text>
                          </Flex>
                        )}
                      </Box>
                    </Box>

                    {/* Right Column: The Cases */}
                    <Box bg="white" borderRadius="md" shadow="sm" borderWidth="1px" overflow="hidden" height="600px" display="flex" flexDirection="column">
                      <Box px={6} py={4} borderBottomWidth="1px" borderColor="gray.200" bg="gray.50">
                        <Heading size="sm" color="gray.800">Flagged Cases ({processedData.needsReviewCases.length})</Heading>
                      </Box>
                      <Box overflowY="auto" flex="1" p={4}>
                        {processedData.needsReviewCases.length === 0 ? (
                          <Text color="gray.500" textAlign="center" mt={10}>No cases flagged for review in this period.</Text>
                        ) : (
                          <VStack spacing={4} align="stretch">
                            {processedData.needsReviewCases.map((item, idx) => (
                              <Box key={idx} p={4} borderWidth="1px" borderRadius="md" borderColor="red.200" bg="red.50">
                                <Flex justify="space-between" mb={2}>
                                  <Text 
                                    as="a" 
                                    href={`/ionmform?caseid=${item.external_case_id}`} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    color="blue.600"
                                    fontWeight="bold"
                                    _hover={{ textDecoration: 'underline' }}
                                  >
                                    {item.tech_report_case_id || item.external_case_id.substring(0,8)+'...'}
                                  </Text>
                                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                                    {item.date_of_surgery || (item.created_at ? item.created_at.split('T')[0] : 'N/A')}
                                  </Text>
                                </Flex>
                                <Text fontSize="sm" whiteSpace="pre-wrap" color="gray.700">
                                  {item.claim_notes}
                                </Text>
                              </Box>
                            ))}
                          </VStack>
                        )}
                      </Box>
                    </Box>
                  </SimpleGrid>
                </TabPanel>

                {/* Tab 5: Admission Status */}
                <TabPanel px={0} py={4}>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={6}>
                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                      <Heading size="sm" mb={4} color="gray.600">POS Assignment</Heading>
                      <Box height="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={processedData.admissionStatusChartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{fontSize: 12}} />
                            <YAxis tick={{fontSize: 12}} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#3182CE" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>

                    <Box bg="white" p={4} borderRadius="md" shadow="sm" borderWidth="1px">
                      <Heading size="sm" mb={4} color="gray.600">Confidence Scores</Heading>
                      <Box height="300px">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={processedData.admissionConfidenceChartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="name" tick={{fontSize: 12}} />
                            <YAxis tick={{fontSize: 12}} />
                            <Tooltip />
                            <Bar dataKey="count" fill="#805AD5" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Box>
                    </Box>
                  </SimpleGrid>

                  <Box overflowX="auto" bg="white" borderRadius="md" shadow="sm" borderWidth="1px">
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.50">
                        <Tr>
                          <Th>Date</Th>
                          <Th>Case ID</Th>
                          <Th>Technologist</Th>
                          <Th>Surgeon</Th>
                          <Th>POS</Th>
                          <Th>Confidence</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.length === 0 ? (
                          <Tr>
                            <Td colSpan={6} textAlign="center" py={4}>No cases found</Td>
                          </Tr>
                        ) : (
                          data.map((item, idx) => {
                            const conf = item.admission_confidence;
                            let confColor = "gray";
                            if (conf !== 'Missing') {
                              const confNum = parseInt(conf);
                              if (confNum >= 8) confColor = "green";
                              else if (confNum >= 5) confColor = "yellow";
                              else confColor = "red";
                            }

                            return (
                              <Tr key={idx}>
                                <Td>{item.date_of_surgery || item.created_at?.split('T')[0]}</Td>
                                <Td>
                                  <Button 
                                    variant="link" 
                                    colorScheme="blue" 
                                    size="sm"
                                    onClick={() => window.open(`/ionmform?caseid=${item.external_case_id}`, '_blank')}
                                  >
                                    {item.tech_report_case_id || item.external_case_id}
                                  </Button>
                                </Td>
                                <Td>{item.technologist_name}</Td>
                                <Td>{item.referring_provider}</Td>
                                <Td>
                                  <Badge colorScheme={item.admission_status === 'Missing' ? 'gray' : 'blue'}>
                                    {item.recommended_pos_code && item.recommended_pos_code !== 'Missing' 
                                      ? `POS ${item.recommended_pos_code}` 
                                      : (item.admission_status || 'Missing')}
                                  </Badge>
                                </Td>
                                <Td>
                                  <HStack spacing={2}>
                                    <Badge colorScheme={confColor}>
                                      {conf || 'Missing'}
                                    </Badge>
                                    {conf !== 'Missing' && parseInt(conf) < 8 && (
                                      <Badge colorScheme="red" variant="outline">Needs Review</Badge>
                                    )}
                                  </HStack>
                                </Td>
                              </Tr>
                            );
                          })
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </>
        )}
      </Box>
    </Box>
  );
};

export default NMACodingQualityDashboard;
